import makeStyles from '@mui/styles/makeStyles'

// MT.3 brand refresh — card surfaces are flat with rounded corners.
// The previous chamfer (clip-path polygon) and the gray edge/stack rings
// have been removed; corners now come from theme.radius tokens and the
// floating drop-shadow handles depth. Layout assumes the description is
// always rendered below the title (see useCardExpand) — there is no
// hover-driven collapse to plan for.

export const useStyles = makeStyles(
  ({ palette, typography, spacing, breakpoints, radius, props }) => ({
    root: {
      transition: 'filter 0.2s',
      width: props => (props.fullWidth ? '100%' : undefined),
      // Fill parent height so cards in a flex/grid row stretch to the
      // tallest sibling — see the title comment below for why text-length
      // variance now flexes through to card-level stretching rather than
      // being absorbed by the title's reserved height.
      height: '100%',
      // Hover lift is a CSS `:hover` on the root so every card variant
      // (including PlaylistCard, which doesn't wire `useCardExpand`)
      // gets visual feedback. `filter: drop-shadow` follows the rounded
      // card silhouette automatically, so there's no sharp-rectangle
      // artifact like MUI's CardActionArea overlay produced.
      '&:hover': {
        filter: 'drop-shadow(0px 10px 10px #CCC9CF)'
      },
      // Keep the prop-driven shadow for callers that explicitly opt in
      // (e.g. via `useCardExpand`'s hoverShadow state) — same effect,
      // independent trigger.
      filter: props =>
        props?.hoverShadow && 'drop-shadow(0px 10px 10px #CCC9CF)'
    },
    card: props =>
      props.fullWidth
        ? {
            display: 'flex',
            alignItems: 'stretch',
            width: '100%',
            height: '100%'
          }
        : {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%'
          },
    // MUI's CardActionArea ships a `focusHighlight` overlay that darkens
    // on hover. Since cards already provide hover feedback via
    // `hoverShadow` (drop-shadow on the root) and a focus outline, the
    // dark overlay is redundant and shows a sharp inner rectangle that
    // doesn't follow the card's rounded corners (the action area is
    // inset by the summary padding, so the card's overflow:hidden can't
    // clip it). Always transparent now.
    actionAreaHighlighted: {
      background: 'transparent'
    },
    image: props =>
      props.fullWidth
        ? {
            width: props.right ? 320 : 288,
            minWidth: props.right ? 320 : 288,
            flexShrink: 0,
            // Center only when the image has an explicit fixed height
            // (right-side variant). Image-on-left has height:auto and
            // relies on the card's `alignItems: stretch` to grow with the
            // summary — centering it would collapse it to 0px.
            alignSelf: props.right ? 'center' : 'stretch',
            height: props.right ? spacing(22) : 'auto',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            borderRadius: radius.xl,
            margin: props.right ? spacing(2) : 'inherit'
          }
        : {
            width: '100%',
            height: spacing(22.5),
            objectFit: 'cover',
            '&:hover': {
              cursor: 'pointer'
            }
          },
    summary: props =>
      props.fullWidth
        ? {
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: spacing(35),
            background: '#FFF',
            padding: spacing(3),
            gap: spacing(1)
          }
        : {
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            width: '100%',
            background: '#FFF',
            padding: spacing(2),
            gap: spacing(1)
          },
    // MT.3: title is no longer reserved at a fixed 2-line height. Short
    // titles take one line, the subtitle sits right after, and the actions
    // row stays pinned to the bottom via `actions.marginTop: auto`. Card
    // height stays uniform across siblings via the parent flex/grid
    // (align-items: stretch) rather than via padded text — so a row of
    // cards still aligns even when their text lengths differ.
    title: props =>
      props.fullWidth
        ? {
            ...typography.body.l,
            fontWeight: typography.weight.bold,
            ...multiNoWrap(2)
          }
        : {
            ...typography.body.l,
            fontWeight: typography.weight.bold,
            ...multiNoWrap(2)
          },
    subtitle: props =>
      props.fullWidth
        ? {
            marginTop: spacing(0.5),
            ...typography.body.m,
            fontWeight: typography.weight.regular,
            ...multiNoWrap(2)
          }
        : {
            marginTop: spacing(1),
            ...typography.body.m,
            fontWeight: typography.weight.regular,
            minHeight: props.isPlaylist ? `${typography.body.m.lineHeight * 3}em` : 'auto',
            ...multiNoWrap(3)
          },
    header: {
      maxWidth: '100%',
      marginBottom: spacing(1)
    },
    info: {
      color: palette.text.secondary,
      fontWeight: typography.fontWeightMedium,
      '& svg': {
        marginRight: spacing(0.5)
      }
    },
    time: {
      color: palette.text.secondary,
      fontWeight: typography.fontWeightBold,
      // `& svg` instead of `.MuiSvgIcon-root` so Phosphor icons (which
      // render plain SVG without the MUI class) pick up the same spacing.
      '& svg': {
        fontSize: 18,
        marginRight: spacing(0.5)
      }
    },
    actions: {
      marginTop: 'auto',
      paddingTop: spacing(1)
    },
    featuredIcon: {
      color: palette.common.white
    },
    featuredPill: {
      backgroundColor: palette.primary.main,
      color: palette.common.white,
      borderRadius: spacing(0.5),
      padding: `${spacing(0.25)} ${spacing(0.75)}`,
      fontSize: typography.body.s.fontSize,
      fontWeight: typography.fontWeightRegular,
      display: 'inline-block',
      width: 'auto',
      '& .MuiSvgIcon-root': {
        display: 'none'
      }
    }
  })
)

const multiNoWrap = lines => {
  return {
    lineClamp: lines,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical'
  }
}
